import { useRef, useState } from 'react';
import { FileText, Loader2, UploadCloud, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MAX_PDF_BYTES } from '@/lib/po-draft-utils';

interface Props { disabled: boolean; extracting: boolean; fileName: string; error: string; onFile: (f: File) => void; onCancel: () => void; onRetry?: () => void }

export function UploadZone({ disabled, extracting, fileName, error, onFile, onCancel, onRetry }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [localErr, setLocalErr] = useState('');
  const take = (f?: File) => {
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) return setLocalErr('Only PDF files are accepted.');
    if (f.size > MAX_PDF_BYTES) return setLocalErr('PDF is larger than 10MB.');
    setLocalErr(''); onFile(f);
  };
  const err = localErr || error;
  return (
    <div className="space-y-3">
      <div role="button" tabIndex={0} data-testid="dropzone-pdf"
        onClick={() => !disabled && !extracting && input.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !disabled && !extracting && input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled && !extracting) take(e.dataTransfer.files[0]); }}
        className={`rounded-lg border-2 border-dashed px-6 py-14 text-center transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${over ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50'}`}>
        {extracting ? (
          <div className="space-y-2"><Loader2 className="w-7 h-7 mx-auto animate-spin text-primary" />
            <div className="text-sm font-medium" data-testid="text-extracting">Reading {fileName}…</div>
            <div className="text-xs text-muted-foreground">Extracting the order and matching customers and SKUs. This can take up to a minute.</div>
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onCancel(); }} data-testid="button-cancel-extract"><X className="w-3.5 h-3.5 mr-1" />Cancel</Button></div>
        ) : (
          <div className="space-y-2"><UploadCloud className="w-7 h-7 mx-auto text-muted-foreground" />
            <div className="text-sm font-medium">{disabled ? 'Select a store first' : 'Drop a customer purchase order PDF here, or click to browse'}</div>
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-1"><FileText className="w-3.5 h-3.5" />PDF only, up to 10MB. Extracted with AI; not saved in SoraFlo.</div></div>
        )}
        <input ref={input} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="input-pdf" onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }} />
      </div>
      {err && (
        <div className="rounded border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive flex items-center gap-3" data-testid="text-extract-error">
          <span className="flex-1">{err}</span>{error && onRetry && <Button size="sm" variant="outline" onClick={onRetry} data-testid="button-retry-extract">Retry</Button>}
        </div>
      )}
    </div>
  );
}
